import { getCountryWithEmoji } from 'constants/country';
import { REPORT_QA_PREVIEW, REQUEST_REPORT_V2 } from 'constants/routePaths';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { NetworkStatus, useQuery } from '@apollo/client';
import {
  ReportsQaCountsQuery,
  ReportsQaCountsResponse,
  ReportsQaPageQuery,
  ReportsQaPageResponse,
  ReportsQaRow,
} from '@graphql/queries/ReportsQaQuery';
import CreateOutlined from '@mui/icons-material/CreateOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import RDTFirstPage from 'components/ReactDataTable/RDTFirstPage';
import RDTLastPage from 'components/ReactDataTable/RDTLastPage';
import RTDNextPage from 'components/ReactDataTable/RDTNextPage';
import RDTPrevPage from 'components/ReactDataTable/RDTPrevPage';
import { getReportParam } from 'components/Report/ReportDemographics/utils';
import ReportDownloadIcon from 'components/Report/ReportDownloadIcon';
import { isArchtypeReportId } from 'components/Report/utils';
import ReportViewerMenu from 'components/ReportViewerMenu';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import useUpdateReportQa, { UpdateReportQa } from 'hooks/useUpdateReportQa';
import useWindowSize from 'hooks/useWindowSize';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Optional } from 'typescript-optional';
import formatPath from 'utils/formatPath';
import { isSuccessful } from 'utils/reportUtils';

import EditReportProjectNameModal from './EditReportProjectNameModal';
import EditReportWorkspaceModal from './EditReportWorkspaceModal';
import { QaStatusChip, ReportStatusChip } from './QaStatusChip';
import {
  buildOrderBy,
  buildReportsQaCountFilters,
  buildReportsQaFilter,
  QA_STATUS_FILTERS,
  QaStatusFilter,
  SORTABLE_COLUMNS,
} from './reportsQaFilters';
import styles from './ReportsQATable.module.css';
import useReportsQaParams, {
  ROWS_PER_PAGE_OPTIONS,
} from './useReportsQaParams';
import { formatReportType } from '../../constants/report';
import { PageHeader } from '../../styles/themeComponents';

const DATE_FORMAT = 'yyyy-MM-dd HH:mm';
const SEARCH_DEBOUNCE_MS = 350;

const nullToUndefined = <T,>(value: T | null): T | undefined =>
  value ?? undefined;

interface ColumnHandlers {
  t: (key: string) => string;
  returnTo: string;
  onEditWorkspace: (row: ReportsQaRow) => void;
  onEditProjectName: (row: ReportsQaRow) => void;
  updateReportQa: UpdateReportQa;
  widths: ReturnType<typeof useWindowSize>;
}

const buildColumns = ({
  t,
  returnTo,
  onEditWorkspace,
  onEditProjectName,
  updateReportQa,
  widths,
}: ColumnHandlers): TableColumn<ReportsQaRow>[] => [
  {
    id: 'workspace',
    minWidth: '140px',
    name: t('reports.workspace'),
    wrap: true,
    omit: widths.widthBelow1000,
    cell: (row) => (
      <div className={styles.editableCell}>
        <span>{row.producerByWorkspaceId?.name}</span>
        {row.passedQa !== true && (
          <IconButton
            size="small"
            className={styles.editButton}
            aria-label={t('reportsQa.edit.workspaceTitle')}
            onClick={() => onEditWorkspace(row)}
          >
            <CreateOutlined color="primary" fontSize="small" />
          </IconButton>
        )}
      </div>
    ),
  },
  {
    id: 'name',
    minWidth: '150px',
    name: t('panel.projectName'),
    sortable: true,
    wrap: true,
    grow: 2,
    cell: (row) => (
      <div className={styles.editableCell}>
        <span className={styles.projectName}>{row.projectName}</span>
        {row.passedQa !== true && (
          <IconButton
            size="small"
            className={styles.editButton}
            aria-label={t('reportsQa.edit.projectNameTitle')}
            onClick={() => onEditProjectName(row)}
          >
            <CreateOutlined color="primary" fontSize="small" />
          </IconButton>
        )}
      </div>
    ),
  },
  {
    id: 'reportType',
    name: t('reports.reportType'),
    sortable: true,
    wrap: true,
    minWidth: '100px',
    omit: widths.widthBelow1000,
    cell: (row) => formatReportType(row.reportType),
  },
  {
    id: 'targetGroup',
    minWidth: '130px',
    name: t('reports.targetGroup'),
    sortable: true,
    wrap: true,
    omit: widths.widthBelow1000,
    cell: (row) => (
      <div className={styles.stackedCell}>
        <span>{row.targetGroupName}</span>
        <span className={styles.secondaryText}>
          {getCountryWithEmoji(
            getReportParam<string | string[]>(
              Optional.ofNullable(row.params),
              'countries',
            ),
          )}
        </span>
      </div>
    ),
  },
  {
    id: 'user',
    minWidth: '120px',
    name: t('reports.submitter'),
    sortable: true,
    wrap: true,
    omit: widths.widthBelow1100,
    cell: (row) => row.requestedThrough,
  },
  {
    id: 'submittedOn',
    name: t('reports.submittedOn'),
    sortable: true,
    width: '120px',
    omit: widths.widthBelow1400,
    cell: (row) =>
      row.startedAt ? formatDate(parseISO(row.startedAt), DATE_FORMAT) : '',
  },
  {
    // Run status until the report succeeds, then its QA outcome
    id: 'status',
    name: t('reportsQa.qa.column'),
    sortable: true,
    width: '150px',
    cell: (row) =>
      isSuccessful(row) ? (
        <QaStatusChip
          reportStatus={row.reportStatus}
          passedQa={row.passedQa}
          mapOnly={row.mapOnly}
        />
      ) : (
        <ReportStatusChip status={row.reportStatus} />
      ),
  },
  {
    id: 'pdf',
    name: t('general.pdf'),
    width: '60px',
    cell: (row) =>
      isSuccessful(row) && !isArchtypeReportId(row.reportId) ? (
        <ReportDownloadIcon reportId={row.reportId} pdfIcon />
      ) : null,
  },
  {
    id: 'action',
    name: t('general.action'),
    width: '160px',
    cell: (row) => (
      <div className={styles.actionCell}>
        {isSuccessful(row) && (
          <Button
            size="small"
            variant={row.passedQa === null ? 'contained' : 'outlined'}
            color="secondary"
            disableElevation
            component={Link}
            to={{
              pathname: formatPath(REPORT_QA_PREVIEW, {
                reportId: row.reportId,
              }),
              state: { from: returnTo },
            }}
          >
            {row.passedQa === null
              ? t('reportsQa.review')
              : t('reportsQa.preview.open')}
          </Button>
        )}
        <ReportViewerMenu report={row} updateReportJobStatus={updateReportQa} />
      </div>
    ),
  },
];

const ReportsQATable: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const widths = useWindowSize();
  const [params, setParams] = useReportsQaParams();
  const [searchInput, setSearchInput] = useState(params.search);
  const [workspaceEditReport, setWorkspaceEditReport] =
    useState<ReportsQaRow>(null);
  const [projectEditReport, setProjectEditReport] =
    useState<ReportsQaRow>(null);

  // Debounce typing before it hits the URL (and the server)
  useEffect(() => {
    if (searchInput === params.search) return undefined;
    const timeout = setTimeout(
      () => setParams({ search: searchInput, page: 1 }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const pageVariables = useMemo(
    () => ({
      first: params.perPage,
      offset: (params.page - 1) * params.perPage,
      orderBy: buildOrderBy(params.sort, params.dir),
      filter: nullToUndefined(buildReportsQaFilter(params.status, params.search)),
    }),
    [params],
  );

  const countVariables = useMemo(() => {
    const filters = buildReportsQaCountFilters(params.search);
    return Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [
        key,
        nullToUndefined(value),
      ]),
    );
  }, [params.search]);

  const {
    data: pageData,
    previousData: previousPageData,
    error,
    networkStatus,
    refetch: refetchPage,
  } = useQuery<ReportsQaPageResponse>(ReportsQaPageQuery, {
    variables: pageVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: countsData,
    previousData: previousCountsData,
    refetch: refetchCounts,
  } = useQuery<ReportsQaCountsResponse>(ReportsQaCountsQuery, {
    variables: countVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const refreshAll = useCallback(() => {
    refetchPage();
    refetchCounts();
  }, [refetchPage, refetchCounts]);

  const { updateReportQa } = useUpdateReportQa();
  const updateAndRefresh = useCallback<UpdateReportQa>(
    async (...args) => {
      const saved = await updateReportQa(...args);
      if (saved) refreshAll();
      return saved;
    },
    [updateReportQa, refreshAll],
  );

  const reportsPage = (pageData ?? previousPageData)?.reports;
  const rows = reportsPage?.nodes ?? [];
  const totalRows = reportsPage?.totalCount ?? 0;
  const counts = countsData ?? previousCountsData;
  const isFetching = networkStatus !== NetworkStatus.ready;

  // If an action empties the last page, step back a page
  useEffect(() => {
    if (pageData && rows.length === 0 && params.page > 1 && totalRows > 0) {
      setParams({ page: Math.ceil(totalRows / params.perPage) });
    }
  }, [pageData]);

  const returnTo = `${location.pathname}${location.search}`;

  const columns = useMemo(
    () =>
      buildColumns({
        t,
        returnTo,
        onEditWorkspace: setWorkspaceEditReport,
        onEditProjectName: setProjectEditReport,
        updateReportQa: updateAndRefresh,
        widths,
      }),
    [
      t,
      returnTo,
      updateAndRefresh,
      widths.widthBelow1000,
      widths.widthBelow1100,
      widths.widthBelow1400,
    ],
  );

  const handleSort = useCallback(
    (column: TableColumn<ReportsQaRow>, direction: 'asc' | 'desc') => {
      const sort = String(column.id);
      if (SORTABLE_COLUMNS[sort]) setParams({ sort, dir: direction, page: 1 });
    },
    [setParams],
  );

  const handleEditSaved = () => {
    setWorkspaceEditReport(null);
    setProjectEditReport(null);
    refreshAll();
  };

  return (
    <div>
      {workspaceEditReport && (
        <EditReportWorkspaceModal
          report={workspaceEditReport}
          onClose={() => setWorkspaceEditReport(null)}
          onSaved={handleEditSaved}
        />
      )}
      {projectEditReport && (
        <EditReportProjectNameModal
          report={projectEditReport}
          onClose={() => setProjectEditReport(null)}
          onSaved={handleEditSaved}
        />
      )}

      <Paper className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.headerTextContainer}>
            <PageHeader>{t('navigation.reportsQa')}</PageHeader>
            <h3 className={styles.productTitle}>{t('reports.requests')}</h3>
          </div>
          <Link to={REQUEST_REPORT_V2}>
            <MaterialButton variant="outlined" soft teal>
              {t('reports.createReportRequest')}
            </MaterialButton>
          </Link>
        </div>

        <div className={styles.toolbar}>
          <Tabs
            value={params.status}
            onChange={(_, status: QaStatusFilter) =>
              setParams({ status, page: 1 })
            }
            variant="scrollable"
            scrollButtons="auto"
            textColor="secondary"
            indicatorColor="secondary"
          >
            {QA_STATUS_FILTERS.map((status) => (
              <Tab
                key={status}
                value={status}
                className={styles.filterTab}
                label={
                  <span className={styles.filterLabel}>
                    {t(`reportsQa.filters.${status}`)}
                    {counts && (
                      <Chip
                        size="small"
                        className={styles.filterCount}
                        label={counts[status]?.totalCount ?? 0}
                      />
                    )}
                  </span>
                }
              />
            ))}
          </Tabs>
          <div className={styles.toolbarActions}>
            <TextField
              size="small"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('reportsQa.searchPlaceholder')}
              inputProps={{ 'aria-label': t('general.search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={t('reportsQa.refresh')}>
              <span>
                <IconButton onClick={refreshAll} disabled={isFetching}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className={styles.progress}>
          {isFetching && <LinearProgress color="secondary" />}
        </div>

        {error && !reportsPage ? (
          <h3>{t('reportsQa.loadError')}</h3>
        ) : (
          <DataTable
            // Remount on filter/search change so paging restarts at page 1
            key={`${params.status}|${params.search}`}
            columns={columns}
            data={rows}
            keyField="id"
            progressPending={!reportsPage}
            noDataComponent={
              <div className={styles.emptyState}>{t('reportsQa.empty')}</div>
            }
            striped
            highlightOnHover
            fixedHeader
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={params.page}
            paginationPerPage={params.perPage}
            paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onChangePage={(page) => setParams({ page })}
            onChangeRowsPerPage={(perPage, page) =>
              setParams({ perPage, page })
            }
            sortServer
            onSort={handleSort}
            defaultSortFieldId={params.sort}
            defaultSortAsc={params.dir === 'asc'}
            paginationIconFirstPage={<RDTFirstPage />}
            paginationIconLastPage={<RDTLastPage />}
            paginationIconPrevious={<RDTPrevPage />}
            paginationIconNext={<RTDNextPage />}
          />
        )}
      </Paper>
    </div>
  );
};

export default ReportsQATable;
